import { useMemo } from 'react';
import {
  WebBodyText16,
  WebBodyText14,
  WebBodyText12,
  WebHeading28,
  WebHeading32,
} from '@fintronners/react-ui/src/GlobalStyling/webTypography';

import { shortenNumberNumbro } from '@fintronners/react-utils/src/numberUtilsTSX';
import getDynamicStockSymbolAsData from '@fintronners/react-utils/src/dynamicStockSymbolAsData';
import { ImageWithFallback } from '@/components/common/ImageWithFallback';
import { useTranslation } from 'react-i18next';
import AssetActions from '@/components/common/AssetActions/AssetActions';
import { useGetFixedIncomeHeaderSectionQuery } from '@fintronners/react-api/src';
import { convertISODateStringToDisplayString } from '@fintronners/react-utils/src/dateUtils';
import { getBondSymbol } from '@fintronners/internal-core/src/screeners/utils';
import { getAskBidOverview } from '@fintronners/internal-core/src/trade/utils';
import { AssetPriceType } from '@fintronners/react-api/src';

type FixedIncomeOverviewProps = {
  securityId?: string;
  longNameFormat?: boolean;
  noActions?: boolean;
  showBidAsk?: boolean;
};

/**
 * Fixed Income Overview Component
 *
 * This component displays an overview of a fixed income security, including its issuer, yield to maturity, maturity date, and logo.
 * It also provides an option to show bid and ask prices if available.
 */
const FixedIncomeOverview = ({
  securityId,
  longNameFormat = false,
  noActions = false,
  showBidAsk = false,
}: FixedIncomeOverviewProps) => {
  const { t } = useTranslation();

  const { data, loading } = useGetFixedIncomeHeaderSectionQuery({
    skip: !securityId,
    fetchPolicy: 'no-cache',
    variables: {
      securityID: securityId || '',
    },
  });

  const securityAsset = data?.securityAssets?.edges?.[0]?.node;

  const details =
    securityAsset?.details?.__typename === 'SecurityFixedIncomeDetails'
      ? securityAsset?.details
      : undefined;

  const normalizedData = useMemo(
    () => ({
      issuer: details?.issuer || '',
      yieldMaturity: details?.yieldMaturity || 0,
      dynamicYtm:
        securityAsset?.fixedIncomeMarket?.lastPrices?.find(
          (price) => price.type === AssetPriceType.AssetPriceTypeYieldToMaturity,
        )?.price || 0,
      maturityDate:
        convertISODateStringToDisplayString(details?.maturityDate, '/', '2-digit') || '',
      logoURI: securityAsset?.logoURI || '',
      symbol: getBondSymbol(securityAsset?.symbol),
    }),
    [details, securityAsset],
  );

  const assetMedia = useMemo(
    () => ({
      logoURI: normalizedData.logoURI,
      symbol: normalizedData.symbol,
      issuer: normalizedData.issuer,
    }),
    [normalizedData],
  );

  const encodedSvg = getDynamicStockSymbolAsData(normalizedData.symbol ?? '');
  const fallbackSrc = `data:image/svg+xml;charset=utf-8,${encodedSvg}`;

  const bidAskOverview = useMemo(() => {
    if (!showBidAsk) return null;
    return getAskBidOverview(securityAsset?.fixedIncomeMarket, details?.parValue);
  }, [showBidAsk, securityAsset, details]);

  const bidPrice = t('components.FixedIncomeOverview.bidPrice', {
    price: bidAskOverview?.bidPrice,
    ytm: bidAskOverview?.bidYTM,
    context: bidAskOverview?.bidPrice ? undefined : 'empty',
  });

  const bidQuantity = t('components.FixedIncomeOverview.bidQty', {
    quantity: bidAskOverview?.bidQuantity,
    min: bidAskOverview?.bidMinimumQuantity,
  });

  const askPrice = t('components.FixedIncomeOverview.askPrice', {
    price: bidAskOverview?.askPrice,
    ytm: bidAskOverview?.askYTM,
    context: bidAskOverview?.askPrice ? undefined : 'empty',
  });

  const askQuantity = t('components.FixedIncomeOverview.askQty', {
    quantity: bidAskOverview?.askQuantity,
    min: bidAskOverview?.askMinimumQuantity,
  });

  if (loading) {
    return (
      <div className="flex flex-col gap-3">
        <div className="flex gap-3 items-center">
          <div className="h-10 w-10 animate-pulse rounded-full border-grey25 border bg-gray-300" />
          <div className="h-7 w-40 animate-pulse bg-gray-300" />
        </div>

        <div className="h-4 w-40 animate-pulse bg-gray-300" />
        <div className="flex flex-col gap-3">
          <div className="h-8 w-20 animate-pulse bg-gray-300" />
        </div>

        {showBidAsk && (
          <div className="flex flex-col gap-1">
            <div className="h-3 w-64 animate-pulse bg-gray-300" />
            <div className="h-3 w-64 animate-pulse bg-gray-300" />
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div className="flex justify-between relative">
        <div className="flex flex-col w-full">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-xs text-muted-foreground space-x-2">
              <div className="rounded-full border-grey25 border min-w-10 min-h-10 h-10 w-10 relative">
                <ImageWithFallback
                  className="p-1"
                  src={assetMedia?.logoURI || fallbackSrc}
                  fallbackSrc={fallbackSrc}
                  alt={assetMedia.symbol ?? '-'}
                  style={{ objectFit: 'contain', objectPosition: 'center' }}
                  fill
                />
              </div>

              <WebHeading32.Bold className="text-primary">
                {normalizedData.symbol} {normalizedData.maturityDate}
              </WebHeading32.Bold>
            </div>

            <div className="absolute right-0 bottom-1 lg:relative">
              {!noActions && <AssetActions isFixedIncome securityId={securityId ?? ''} />}
            </div>
          </div>

          <WebBodyText16.Regular className="text-grey55">
            {t('components.FixedIncomeOverview.assetName', {
              issuer: normalizedData.issuer,
              yieldMaturity: shortenNumberNumbro(normalizedData.yieldMaturity),
              maturityDate: convertISODateStringToDisplayString(details?.maturityDate, '/'),
              context: longNameFormat ? undefined : 'short',
            })}
          </WebBodyText16.Regular>

          <div className="flex items-center gap-2 mt-[10px]">
            <WebHeading28.Bold className="text-darkestBlue">
              {shortenNumberNumbro(normalizedData.dynamicYtm).concat('%')}
            </WebHeading28.Bold>

            <div className="bg-grey6 px-2 py-1 rounded-[4px]">
              <WebBodyText14.Bold className="text-grey72">{t('common.ytm')}</WebBodyText14.Bold>
            </div>
          </div>
        </div>
      </div>

      {showBidAsk && bidAskOverview && (
        <div className="flex flex-col mt-2">
          <WebBodyText12.Regular className="text-grey55">
            {`${bidPrice} | ${bidQuantity}`}
          </WebBodyText12.Regular>

          <WebBodyText12.Regular className="text-grey55">
            {`${askPrice} | ${askQuantity}`}
          </WebBodyText12.Regular>
        </div>
      )}
    </div>
  );
};

export default FixedIncomeOverview;
